const BASE = "https://web-production-c0e77.up.railway.app";

// Oturum token'ı — AuthContext her değişimde setAuthToken ile günceller.
// getPredictions artık kullanıcıya özel (kilidi açma modeli), token gerekiyor.
let _authToken = null;
export function setAuthToken(t) { _authToken = t || null; }
function authHeader() { return _authToken ? { Authorization: `Bearer ${_authToken}` } : {}; }

/**
 * Admin secret'ini header olarak paketler.
 *
 * Secret ASLA query string'e konmamali — orada proxy/erisim loglarina,
 * tarayici gecmisine ve Referer basligina sizar. Backend hem header'i hem
 * (geriye donuk uyumluluk icin) ?secret= parametresini kabul eder; frontend
 * her zaman header kullanir.
 */
function adminHeaders(secret, extra = {}) {
  return secret ? { ...extra, "X-Admin-Secret": secret } : extra;
}

// Predict: job başlat, 3sn'de bir poll et, done gelince resolve et
async function predictWithPolling(home, away, league, aiProvider = "gpt") {
  // 1. Job başlat — anında job_id döner, bağlantı kapanır
  const res = await fetch(`${BASE}/api/predict`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ home, away, league, ai_provider: aiProvider }),
  });
  const { job_id } = await res.json();

  // 2. Poll et — 3sn'de bir job durumunu sorgula
  return new Promise((resolve, reject) => {
    let attempts = 0;
    const MAX_ATTEMPTS = 60; // max 3 dakika (60 x 3sn)
    const poll = async () => {
      attempts++;
      if (attempts > MAX_ATTEMPTS) {
        reject(new Error("Analiz zaman asimi (3 dakika)"));
        return;
      }
      try {
        const r = await fetch(`${BASE}/api/predict/job/${job_id}`);
        const job = await r.json();
        if (job.status === "done") {
          resolve(job.result);
        } else if (job.status === "error") {
          reject(new Error(job.error || "Analiz hatasi"));
        } else {
          setTimeout(poll, 3000);
        }
      } catch (e) {
        setTimeout(poll, 3000);
      }
    };
    setTimeout(poll, 3000);
  });
}

export const api = {
  leagues: () => fetch(`${BASE}/api/leagues`).then(r => r.json()),
  standings: (league) => fetch(`${BASE}/api/standings/${encodeURIComponent(league)}`).then(r => r.json()),
  matches: (league, limit = 50, season) => fetch(
    `${BASE}/api/matches/${encodeURIComponent(league)}?limit=${limit}` +
    (season ? `&season=${encodeURIComponent(season)}` : "")
  ).then(r => r.json()),
  teams: (league) => fetch(`${BASE}/api/teams/${encodeURIComponent(league)}`).then(r => r.json()),
  stats: (league, season) => fetch(
    `${BASE}/api/stats/${encodeURIComponent(league)}` +
    (season ? `?season=${encodeURIComponent(season)}` : "")
  ).then(r => r.json()),
  seasons: (league) => fetch(`${BASE}/api/seasons/${encodeURIComponent(league)}`).then(r => r.json()),
  insights: () => fetch(`${BASE}/api/insights`).then(r => r.json()),
  h2h: (home, away, league) => fetch(`${BASE}/api/h2h?home=${encodeURIComponent(home)}&away=${encodeURIComponent(away)}&league=${encodeURIComponent(league)}`).then(r => r.json()),
  getToday: () => fetch(`${BASE}/api/today`).then(r => r.json()),
  predict: (home, away, league, aiProvider) => predictWithPolling(home, away, league, aiProvider),
  update: (secret) => fetch(`${BASE}/api/update`, { headers: adminHeaders(secret) }).then(r => r.json()),
  // Herkese açık referans tahminler (Süper Lig) — giriş GEREKTİRMEZ.
  // Haftalık otomatik analizin "AI oyuncu" çıktısı; herkes görür.
  getPublicPredictions: (league) =>
    fetch(`${BASE}/api/predictions/public${league ? `?league=${encodeURIComponent(league)}` : ""}`)
      .then(r => r.ok ? r.json() : { predictions: [] })
      .catch(() => ({ predictions: [] })),
  // Kullanıcıya özel: giriş yoksa boş döner (backend 401 verir), oturum
  // token'ı ile yalnızca kullanıcının açtığı tahminler gelir.
  getPredictions: (league) => {
    if (!_authToken) return Promise.resolve({ predictions: [] });
    return fetch(`${BASE}/api/predictions${league ? `?league=${encodeURIComponent(league)}` : ""}`,
      { headers: authHeader() })
      .then(r => r.ok ? r.json() : { predictions: [] })
      .catch(() => ({ predictions: [] }));
  },
  deletePrediction: (id, secret) => fetch(`${BASE}/api/predictions/${id}`, {
    method: "DELETE", headers: adminHeaders(secret),
  }).then(r => r.json()),
  // Admin: TÜM tahminleri listele (kullanıcı/unlock filtresi yok, service key).
  adminListPredictions: (secret, league) => fetch(
    `${BASE}/api/admin/predictions${league ? `?league=${encodeURIComponent(league)}` : ""}`,
    { headers: adminHeaders(secret) }
  ).then(r => r.ok ? r.json() : { predictions: [] }).catch(() => ({ predictions: [] })),
  // Admin: bir ligin tahminlerini toplu sil (isteğe bağlı tarih aralığı).
  adminDeleteByLeague: (secret, league, opts = {}) => {
    const q = new URLSearchParams({ league });
    if (opts.after) q.set("after", opts.after);
    if (opts.before) q.set("before", opts.before);
    return fetch(`${BASE}/api/admin/predictions/by-league?${q.toString()}`, {
      method: "DELETE", headers: adminHeaders(secret),
    }).then(r => r.json());
  },
  resetPredictions: (secret) => fetch(`${BASE}/api/predictions/reset`, {
    method: "DELETE", headers: adminHeaders(secret),
  }).then(r => r.json()),
  adminGetSettings: (secret) => fetch(`${BASE}/api/admin/settings`, {
    headers: adminHeaders(secret),
  }).then(r => r.json()),
  adminSaveSettings: (secret, payload) => fetch(`${BASE}/api/admin/settings`, {
    method: "POST",
    headers: adminHeaders(secret, { "Content-Type": "application/json" }),
    body: JSON.stringify(payload),
  }).then(r => r.json()),
  // leagues verilirse (dizi) YALNIZCA o ligler analiz edilir (active_leagues
  // yok sayılır); verilmezse backend active_leagues'i kullanır.
  adminTriggerWeekly: (secret, leagues) => {
    const qs = (leagues && leagues.length)
      ? `?leagues=${encodeURIComponent(leagues.join(","))}` : "";
    return fetch(`${BASE}/api/admin/trigger-weekly-analysis${qs}`, {
      method: "POST", headers: adminHeaders(secret),
    }).then(r => r.json());
  },
  getLeagueStats: (league) => fetch(`${BASE}/api/league-stats${league ? `?league=${encodeURIComponent(league)}` : ""}`).then(r => r.json()),
  upsertPrediction: (data) => fetch(`${BASE}/api/predictions/upsert`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  }).then(r => r.json()),
  queryAnalytics: (data) => fetch(`${BASE}/api/analytics/structured`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  }).then(r => r.json()),
};
